import type { ApiClient } from "@planner/core";
import { useState, type ReactNode } from "react";
import { useDevicePush } from "../components/device-push.js";
import { useLocale } from "../i18n/provider.js";
import { platform } from "../platform/index.js";
import { Icon } from "./icons.js";

/**
 * **سؤالُ التنبيه أوّلَ دخولٍ على كل جهاز — بالموك أب المعتمَد (٢٠٢٦-١١-٠٣).**
 *
 * وسأل شادي: «ليه التطبيق ما يسألش لوحده؟». **والمتصفّحُ لا يسمح بسؤال الجهاز
 * بلا ضغطة** (والآيفون يشترطها)، فالسؤالُ الأوّل سؤالُنا نحن، **و«فعّل» هي
 * الضغطةُ التي يفتح بها الجهازُ سؤالَه**.
 *
 * **ومرّةً واحدةً لكل جهاز** — فـ«ليس الآن» تُحفَظ في هذا الجهاز وحده: من رفضه
 * على حاسبه يُسأل على هاتفه، **وسؤالٌ يتكرّر كل دخولٍ يُعلِّم الناسَ ضغطَ
 * «ليس الآن» بلا قراءة**. والزرُّ باقٍ في «حسابي».
 *
 * **ولا يُعرَض** حين التنبيهُ مفعّلٌ هنا، أو محظور، أو غيرُ مدعوم، أو المفتاحُ
 * غيرُ مضبوط — فسؤالٌ يُفضي إلى رفضٍ أسوأ من غيابه.
 */
const ASKED = "push.asked";

export function PushAsk({
  client,
  pushPublicKey,
}: {
  readonly client: ApiClient;
  readonly pushPublicKey: string;
}): ReactNode {
  const { t } = useLocale();
  /** **ولا قائمةَ هنا** — السؤالُ يحتاج «هل هذا الجهازُ مفعَّل» وحده */
  const push = useDevicePush(pushPublicKey, {
    save: (body) => client.request("/me/push/devices", { method: "POST", body }),
  });
  const [answered, setAnswered] = useState(() => platform.readPreference(ASKED) === "1");

  if (
    answered ||
    !push.supported ||
    !push.configured ||
    platform.pushBlocked ||
    !push.known ||
    push.here !== null
  ) {
    return null;
  }

  const close = (): void => {
    platform.writePreference(ASKED, "1");
    setAnswered(true);
  };

  return (
    <div className="mod push-ask" role="dialog" aria-modal="true" aria-labelledby="push-ask-h">
      <span className="push-ask-ic" aria-hidden="true">
        <Icon name="bell" />
      </span>
      <h2 id="push-ask-h">{t("push.askTitle")}</h2>
      <p>{t("push.askBody")}</p>
      {push.problem !== null ? (
        <p className="blocked-note" role="alert">
          {t(push.problem)}
        </p>
      ) : null}
      <button
        type="button"
        className="btn"
        disabled={push.busy}
        onClick={() => {
          void (async () => {
            /**
             * **ويُغلَق بالنجاح أو بقرار صاحبه** — فمن ضغط «حظر» في سؤال الجهاز
             * قرّر. **وما عداهما يبقى مفتوحاً بسببه** لا يختفي بلا كلمة.
             */
            if ((await push.enable()) || platform.pushBlocked) close();
          })();
        }}
      >
        {t("push.askEnable")}
      </button>
      <button type="button" className="btn gh" data-close disabled={push.busy} onClick={close}>
        {t("push.askLater")}
      </button>
      <p className="field-hint">{t("push.askNote")}</p>
    </div>
  );
}
